import { z } from '@harvestiq/zod';
import {
  GeneratedAlways,
  Selectable,
  Insertable,
  Updateable,
  ColumnType,
} from 'kysely';
import {
  RmaEntityTypeCodes,
  RmaProgramIndicatorCodes,
  RmaTaxIdTypes,
} from '@harvestiq/constants';
import { zDecimalRequired, zPhoneNumber, zTimestamp } from '@harvestiq/zod';
import { expectTypeOf } from 'expect-type';
import { DbColumnDecimalNotNull, DbTimestampGenerated } from '@harvestiq/utils';

export interface EntitiesHistoryTable {
  id: GeneratedAlways<string>;
  ownerId: string | null;
  agentId: string | null;
  primaryEntityId: string | null;
  evergreenEntityId: string | null;
  aipCode: string | null;
  reinsuranceYear: number | null;
  aipPolicyProducerKey: string | null;
  aipPolicyProducerAddressKey: string | null;
  aipPolicyProducerOtherPersonKey: string | null;
  type: RmaEntityTypeCodes;
  ownershipShare: DbColumnDecimalNotNull;
  taxId: string | null;
  taxIdType: RmaTaxIdTypes | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateAbbreviation: string | null;
  zip: string | null;
  zipExtensionCode: string | null;
  phone: string | null;
  phoneNumberExceptionCode: string | null;
  phoneExtensionNumber: string | null;
  internationalAddress: string | null;
  internationalCountryCode: string | null;
  internationalPhoneCountryCode: string | null;
  programIndicator: RmaProgramIndicatorCodes | null;
  isPre2026BeginningFarmerRancher: ColumnType<
    boolean,
    boolean | undefined,
    boolean
  >;
  isVeteranFarmerRancher: ColumnType<boolean, boolean | undefined, boolean>;
  beginningFarmerRancherCropYearCount: ColumnType<
    number | null,
    number | null | undefined,
    number | null
  >;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  title: string | null;
  picCode: string | null;
  locationStateCode: string | null;
  authorizedRepPoa: string | null;
  stateOfIncorporation: string | null;
  businessName: string | null;
  importSourceId: string | null;
  policyNumber: string | null;
  isLatest: boolean | null;
  createdBy: string | null;
  updatedBy: string | null;
  mergedIntoEntityHistoryId: string | null;
  createdAt: DbTimestampGenerated;
  updatedAt: DbTimestampGenerated;
  deletedAt: Date | null;
}

export type DbEntityHistory = Selectable<EntitiesHistoryTable>;
export type DbEntityHistoryInsert = Insertable<EntitiesHistoryTable>;
export type DbEntityHistoryUpdate = Updateable<EntitiesHistoryTable>;

export const dbEntityHistorySchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().uuid().nullable(),
  agentId: z.string().uuid().nullable(),
  primaryEntityId: z.string().uuid().nullable(),
  evergreenEntityId: z.string().uuid().nullable(),
  aipCode: z.string().trim().nullable(),
  reinsuranceYear: z.number().nullable(),
  aipPolicyProducerKey: z.string().trim().nullable(),
  aipPolicyProducerAddressKey: z.string().trim().nullable(),
  aipPolicyProducerOtherPersonKey: z.string().trim().nullable(),
  type: z.nativeEnum(RmaEntityTypeCodes),
  ownershipShare: zDecimalRequired(),
  taxId: z.string().trim().nullable(),
  taxIdType: z.nativeEnum(RmaTaxIdTypes).nullable(),
  email: z.string().trim().nullable(),
  addressLine1: z.string().trim().nullable(),
  addressLine2: z.string().trim().nullable(),
  city: z.string().trim().nullable(),
  stateAbbreviation: z.string().trim().nullable(),
  zip: z.string().trim().nullable(),
  zipExtensionCode: z.string().trim().nullable(),
  phone: zPhoneNumber().nullable(),
  phoneNumberExceptionCode: z.string().trim().nullable(),
  phoneExtensionNumber: z.string().trim().nullable(),
  internationalAddress: z.string().trim().nullable(),
  internationalCountryCode: z.string().trim().nullable(),
  internationalPhoneCountryCode: z.string().trim().nullable(),
  programIndicator: z.nativeEnum(RmaProgramIndicatorCodes).nullable(),
  isPre2026BeginningFarmerRancher: z.boolean(),
  isVeteranFarmerRancher: z.boolean(),
  beginningFarmerRancherCropYearCount: z.number().nullable(),
  firstName: z.string().trim().nullable(),
  middleName: z.string().trim().nullable(),
  lastName: z.string().trim().nullable(),
  suffix: z.string().trim().nullable(),
  title: z.string().trim().nullable(),
  picCode: z.string().trim().nullable(),
  locationStateCode: z.string().trim().nullable(),
  authorizedRepPoa: z.string().trim().nullable(),
  stateOfIncorporation: z.string().trim().nullable(),
  businessName: z.string().trim().nullable(),
  importSourceId: z.string().trim().nullable(),
  policyNumber: z.string().trim().nullable(),
  isLatest: z.boolean().nullable(),
  createdAt: z.coerce.date(),
  createdBy: z.string().trim().nullable(),
  updatedAt: z.coerce.date(),
  updatedBy: z.string().trim().nullable(),
  mergedIntoEntityHistoryId: z.string().trim().nullable(),
  deletedAt: z.coerce.date().nullable(),
});
type DbEntityHistorySchemaType = z.infer<typeof dbEntityHistorySchema>;
expectTypeOf<keyof DbEntityHistorySchemaType>().toEqualTypeOf<
  keyof DbEntityHistory
>();
expectTypeOf<DbEntityHistory>().toMatchTypeOf<
  z.infer<typeof dbEntityHistorySchema>
>();

// Remove the sensitive fields from the entity
export const safeDbEntityHistorySchema = dbEntityHistorySchema.omit({
  taxId: true,
});
export type SafeDbEntityHistory = Selectable<
  z.infer<typeof safeDbEntityHistorySchema>
>;

export const dbEntityHistoryInsertSchema = dbEntityHistorySchema
  .omit({
    id: true,
  })
  .partial()
  // Required fields
  .extend({
    ownershipShare: zDecimalRequired(),
    type: z.nativeEnum(RmaEntityTypeCodes),
    taxIdType: z.nativeEnum(RmaTaxIdTypes).nullish(),
    createdAt: zTimestamp().optional(),
    updatedAt: zTimestamp().optional(),
  });

expectTypeOf<DbEntityHistoryInsert>().toMatchTypeOf<
  z.infer<typeof dbEntityHistoryInsertSchema>
>();

export const dbEntityHistoryUpdateSchema =
  dbEntityHistoryInsertSchema.partial();

expectTypeOf<DbEntityHistoryUpdate>().toMatchTypeOf<
  z.infer<typeof dbEntityHistoryUpdateSchema>
>();
